import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Dashboard.css';
import { ToastContainer, toast } from 'react-toastify';
import DesignCard from './DesignCard';
import 'react-toastify/dist/ReactToastify.css';
import { useCart } from '../context/CartContext';
import { getDisplayName } from '../utils/userUtils';
import OrderHistory from './dashboard/OrderHistory';
import AccountSettings from './dashboard/AccountSettings';

const Dashboard = () => {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [userDesigns, setUserDesigns] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [designToDelete, setDesignToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const { addToCart } = useCart();

  useEffect(() => {
    const loginStatus = searchParams.get('login');
    const error = searchParams.get('error');

    if (loginStatus === 'success') {
      fetchUserInfo();
    } else if (error) {
      console.error('Login error:', error);
      navigate('/auth?error=' + error);
    } else if (!user) {
      navigate('/auth');
    } else {
      setIsLoading(false);
    }
  }, [searchParams, user, navigate, login]);

  const fetchUserInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/protected-route`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.user) {
          login(data.user, token);
          setIsLoading(false);
        } else {
          navigate('/auth');
        }
      } else {
        navigate('/auth');
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
      navigate('/auth');
    }
  };

  const fetchUserDesigns = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/lamp-designs/user/my-designs`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const designs = await response.json();
        const sortedDesigns = designs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setUserDesigns(sortedDesigns);
      }
    } catch (error) {
      console.error('Error fetching user designs:', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchUserDesigns();
    }
  }, [user]);

  const handleRequestDelete = (designId) => {
    setDesignToDelete(designId);
    setShowDeleteModal(true);
  };

  const handleDeleteDesign = async () => {
    if (!designToDelete) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/lamp-designs/${designToDelete}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        setUserDesigns((prev) => prev.filter((d) => d.id !== designToDelete));
        toast.success('Design deleted.');
      } else {
        toast.error('Failed to delete design.');
      }
    } catch (error) {
      toast.error('Error deleting design.');
    }
    setShowDeleteModal(false);
    setDesignToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDesignToDelete(null);
  };

  const handleAddToCart = (design) => {
    const lampProduct = {
      id: `custom-lamp-${design.id}`,
      name: design.name || `Custom Lamp ${design.baseShape?.diameter || ''}x${design.baseShape?.height || ''}mm`,
      price: '$90 AUD',
      description: design.description || 'Your custom designed lamp',
      specifications: {
        dimensions: design.baseShape ? `${design.baseShape.diameter}mm x ${design.baseShape.height}mm` : '',
        material: 'recycled aluminum',
        color: design.rendering?.material?.color || '#fff'
      }
    };
    addToCart(lampProduct);
    toast.success('Added to cart!');
  };

  if (isLoading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'orders', label: 'Orders', icon: '📦' },
    { id: 'designs', label: 'Designs', icon: '✨' },
    { id: 'account', label: 'Account', icon: '⚙️' }
  ];

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Welcome back, {getDisplayName(user)}!</h1>
        <p>Manage your account, orders, and designs</p>
      </div>

      <div className="dashboard-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`dashboard-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="dashboard-content">
        {activeTab === 'overview' && (
          <div className="dashboard-overview">
            <section className="dashboard-section">
              <h2>Quick Actions</h2>
              <div className="quick-actions">
                <Link to="/builder" className="action-card">
                  <div className="action-icon">✨</div>
                  <h3>Build New Lamp</h3>
                  <p>Start designing your custom lamp</p>
                </Link>
                
                <Link to="/shop" className="action-card">
                  <div className="action-icon">🛍️</div>
                  <h3>Browse Shop</h3>
                  <p>Explore our featured designs</p>
                </Link>
                
                <Link to="/cart" className="action-card">
                  <div className="action-icon">🛒</div>
                  <h3>View Cart</h3>
                  <p>Check your shopping cart</p>
                </Link>
              </div>
            </section>

            <section className="dashboard-section">
              <h2>Recent Designs</h2>
              {userDesigns.length > 0 ? (
                <div className="user-designs-grid">
                  {userDesigns.slice(0, 3).map((design) => (
                    <DesignCard
                      key={design.id}
                      design={design}
                      onEdit={() => navigate(`/builder?designId=${design.id}`)}
                      onDelete={() => handleRequestDelete(design.id)}
                      onAddToCart={() => handleAddToCart(design)}
                    />
                  ))}
                </div>
              ) : (
                <div className="no-designs">
                  <p>You haven't created any designs yet.</p>
                  <Link to="/builder" className="cta-button">
                    Create Your First Design
                  </Link>
                </div>
              )}
              {userDesigns.length > 3 && (
                <Link to="#" onClick={() => setActiveTab('designs')} className="view-all-link">
                  View all designs →
                </Link>
              )}
            </section>
          </div>
        )}

        {activeTab === 'orders' && (
          <OrderHistory user={user} />
        )}

        {activeTab === 'designs' && (
          <section className="dashboard-section">
            <h2>Your Designs</h2>
            {userDesigns.length > 0 ? (
              <div className="user-designs-grid">
                {userDesigns.map((design) => (
                  <DesignCard
                    key={design.id}
                    design={design}
                    onEdit={() => navigate(`/builder?designId=${design.id}`)}
                    onDelete={() => handleRequestDelete(design.id)}
                    onAddToCart={() => handleAddToCart(design)}
                  />
                ))}
              </div>
            ) : (
              <div className="no-designs">
                <p>You haven't created any designs yet.</p>
                <Link to="/builder" className="cta-button">
                  Create Your First Design
                </Link>
              </div>
            )}
          </section>
        )}

        {activeTab === 'account' && (
          <AccountSettings user={user} onLogout={async () => { await logout(); navigate('/auth'); }} />
        )}
      </div>

      <ToastContainer position="top-center" />
      
      {showDeleteModal && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: '10px', padding: '2rem', boxShadow: '0 2px 16px rgba(0,0,0,0.2)', maxWidth: 360, width: '90%', textAlign: 'center' }}>
            <h3 style={{ marginBottom: '1rem', color: '#e74c3c' }}>Delete Design?</h3>
            <p style={{ marginBottom: '2rem', color: '#222' }}>Are you sure you want to delete this design? This action cannot be undone.</p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
              <button onClick={handleDeleteDesign} style={{ background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.5rem 1.5rem', fontWeight: 600, cursor: 'pointer' }}>Delete</button>
              <button onClick={handleCancelDelete} style={{ background: '#eee', color: '#333', border: 'none', borderRadius: '4px', padding: '0.5rem 1.5rem', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
